import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Card, CardBody, Col, Row } from 'reactstrap';
import notify from '../../../../../utils/notify';
import {
  formatImprovementClosingDate,
  isImprovementClosingActive,
  resolveImprovementClosingDate,
} from '../../../../../shared/offer-domain/improvement';
import {
  DateCountdown,
  SubmitConfirmModal,
} from '../../../../../shared/offer-domain/ui';
import useSetDyedUndyedImprovement from '../../../hooks/useSetDyedUndyedImprovement';
import useSubmitImprovement from '../../../hooks/useSubmitImprovement';
import { OfferTable } from '../../../te-bridge';
import ActionButton from '../../../../../Components/Common/ActionButton';
import { SkeletonCard } from '../../../../../Components/Common/Skeleton';

const Improvement = ({
  procurement,
  procId,
  parcels = [],
  isLoadingParcels = false,
  isLoadingProcurement = false,
  idOffer: idOfferProp = null,
  isLoadingIdentity: isLoadingIdentityProp = false,
  procurementMitraId = null,
}) => {
  const { saveAlphaImprovement } = useSetDyedUndyedImprovement();
  const [deliveryDetails, setDeliveryDetails] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const idOffer = idOfferProp;
  const isLoadingIdentity = isLoadingIdentityProp;
  const { submitImprovement, isSubmitting } = useSubmitImprovement();

  const handleImprovementMeta = useCallback(
    ({ deliveryDetails: rows } = {}) => {
      setDeliveryDetails(Array.isArray(rows) ? rows : []);
    },
    []
  );

  const closingDate = useMemo(
    () => resolveImprovementClosingDate(deliveryDetails),
    [deliveryDetails]
  );
  const isImprovementOpen = isImprovementClosingActive(closingDate);

  const handleOpenConfirm = () => {
    if (!idOffer) {
      notify.error('Offer ID is missing. Refresh the page and try again.');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    const success = await submitImprovement({ idOffer });
    if (success) setConfirmOpen(false);
  };

  if (isLoadingProcurement && !procurement) {
    return <SkeletonCard lines={6} className='border mb-4' />;
  }

  const isResolvingIdentity = isLoadingProcurement || isLoadingIdentity;

  return (
    <>
      <Card className='border shadow-md mb-4'>
        <CardBody>
          <div className='mb-4'>
            <div className='d-flex flex-wrap align-items-start justify-content-between gap-3'>
              <div>
                <h5 className='fw-bold text-dark mb-1'>Improvement Detail</h5>
                <p className='text-muted small mb-0'>
                  List of offers that have been requested for improvement.
                </p>
              </div>
              <DateCountdown
                label='Closing Date Improvement'
                deadline={closingDate}
                openLabel='Improvement closes in'
                closedLabel='Improvement closed'
              />
            </div>
            {isImprovementOpen ? (
              <Alert color='success' className='mb-0 mt-3'>
                <h6 className='alert-heading mb-1 fw-semibold'>
                  Improvement is in progress
                </h6>
                <p className='mb-0 fs-13'>
                  Improvement is currently in progress and will end on{' '}
                  {formatImprovementClosingDate(closingDate)}.
                </p>
              </Alert>
            ) : (
              <Alert color='danger' className='mb-0 mt-3'>
                <h6 className='alert-heading mb-1 fw-semibold'>
                  Improvement has ended
                </h6>
                <p className='mb-0 fs-13'>
                  Improvement has ended
                  {closingDate
                    ? `. It ended on ${formatImprovementClosingDate(closingDate)}.`
                    : '.'}
                </p>
              </Alert>
            )}
          </div>

          <Row className='mt-3'>
            <Col md={12}>
              <OfferTable
                viewMode='improvement'
                procurementId={procurement?.id || procId}
                procurement={procurement}
                parcels={parcels}
                isLoadingParcels={isLoadingParcels}
                alphaImprovementEditable={isImprovementOpen}
                onAlphaImprovementChange={
                  isImprovementOpen ? saveAlphaImprovement : undefined
                }
                onImprovementMeta={handleImprovementMeta}
                bidderId={procurementMitraId}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      {isImprovementOpen && (
        <div className='req-actionbar flex-wrap d-print-none'>
          <ActionButton
            variant='primary'
            icon='ri-send-plane-line'
            disabled={isResolvingIdentity || !idOffer}
            loading={isSubmitting}
            onClick={handleOpenConfirm}
          >
            Submit Improvement
          </ActionButton>
        </div>
      )}

      <SubmitConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSubmit}
        title='Submit improvement?'
        message='You are about to submit your improvement. After submission, your improvement responses will be sent to the evaluator and this section may become view-only until further action.'
        confirmLabel='Submit improvement'
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default Improvement;
